import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, ArrowUpRight, Briefcase, Building2, CreditCard, Edit3, Eye, EyeOff, FileDown, Gem, LineChart as LineChartIcon, Plus, Search, TrendingDown, TrendingUp, Trash2, Wallet, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";

const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 });
const money2 = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 });

const CATEGORY_LABELS = {
  stocks: "Acciones", etfs: "ETFs", mutual_funds: "Fondos", bonds: "Bonos", crypto: "Cripto",
  real_estate: "Inmuebles", precious_metals: "Metales", cash: "Efectivo", foreign_currency: "Divisas",
  vehicles: "Vehículos", collectibles: "Coleccionables", private_businesses: "Negocios", retirement: "Retiro", other: "Otros",
};
const LIAB_LABELS = { loan: "Préstamo", mortgage: "Hipoteca", credit_card: "Tarjeta", personal_debt: "Deuda personal", business_debt: "Deuda negocio", other: "Otros" };
const PIE_COLORS = ["#4169e1", "#7c3aed", "#10b981", "#f59e0b", "#ef4444", "#06b6d4", "#a855f7", "#eab308", "#22c55e", "#ec4899", "#0ea5e9", "#f97316", "#84cc16", "#6366f1"];

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LineChartIcon },
  { id: "assets", label: "Activos", icon: Gem },
  { id: "liabilities", label: "Pasivos", icon: CreditCard },
  { id: "networth", label: "Net Worth", icon: Wallet },
];

const PRIVACY_KEY = "mc_privacy_mode";

export default function PortfolioPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [privacy, setPrivacy] = useState(() => localStorage.getItem(PRIVACY_KEY) === "1");
  const [dashboard, setDashboard] = useState(null);
  const [assets, setAssets] = useState([]);
  const [liabilities, setLiabilities] = useState([]);
  const [meta, setMeta] = useState(null);
  const [netWorth, setNetWorth] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const togglePrivacy = () => {
    const next = !privacy;
    setPrivacy(next);
    localStorage.setItem(PRIVACY_KEY, next ? "1" : "0");
  };

  const load = async () => {
    setLoading(true);
    try {
      const [d, a, l, m] = await Promise.all([
        api.get("/portfolio/dashboard", { withCredentials: true }),
        api.get("/portfolio/assets", { withCredentials: true }),
        api.get("/portfolio/liabilities", { withCredentials: true }),
        api.get("/portfolio/meta", { withCredentials: true }),
      ]);
      setDashboard(d.data); setAssets(a.data); setLiabilities(l.data); setMeta(m.data);
    } catch (e) { setError(e.response?.data?.detail || "No pudimos cargar el patrimonio."); }
    finally { setLoading(false); }
  };
  const loadNetWorth = () => api.get("/portfolio/net-worth", { withCredentials: true }).then((r) => setNetWorth(r.data));
  useEffect(() => { load(); loadNetWorth(); }, []);

  const createAsset = async () => {
    try {
      const payload = { ...form };
      delete payload.id;
      ["purchase_price", "current_quantity", "current_price", "fees"].forEach((k) => { if (payload[k] !== "" && payload[k] !== undefined) payload[k] = Number(payload[k]); else delete payload[k]; });
      let r;
      if (form.id) {
        r = await api.put(`/portfolio/assets/${form.id}`, payload, { withCredentials: true });
      } else {
        r = await api.post("/portfolio/assets", payload, { withCredentials: true });
      }
      setModal(null); setForm({});
      await load();
      if (!form.id) navigate(`/patrimonio/${r.data.id}`);
    } catch (e) { setError(e.response?.data?.detail || "No pudimos guardar el activo."); }
  };

  const createLiability = async () => {
    try {
      const payload = { ...form };
      delete payload.id;
      ["principal", "current_balance", "interest_rate", "monthly_payment"].forEach((k) => { if (payload[k] !== "" && payload[k] !== undefined) payload[k] = Number(payload[k]); else delete payload[k]; });
      if (form.id) {
        await api.put(`/portfolio/liabilities/${form.id}`, payload, { withCredentials: true });
      } else {
        await api.post("/portfolio/liabilities", payload, { withCredentials: true });
      }
      setModal(null); setForm({}); await load(); await loadNetWorth();
    } catch (e) { setError(e.response?.data?.detail || "No pudimos guardar el pasivo."); }
  };

  const editAsset = (a) => { setForm({ ...a }); setModal("asset"); };
  const editLiability = (l) => { setForm({ ...l }); setModal("liability"); };

  const deleteAsset = async (id) => {
    if (!window.confirm("¿Eliminar este activo y todas sus transacciones?")) return;
    await api.delete(`/portfolio/assets/${id}`, { withCredentials: true });
    await load();
  };

  const deleteLiability = async (id) => {
    if (!window.confirm("¿Eliminar este pasivo?")) return;
    await api.delete(`/portfolio/liabilities/${id}`, { withCredentials: true });
    await load(); await loadNetWorth();
  };

  const saveSnapshot = async () => {
    try { await api.post("/portfolio/net-worth/snapshot", {}, { withCredentials: true }); await loadNetWorth(); }
    catch (e) { setError(e.response?.data?.detail || "No pudimos guardar el snapshot."); }
  };

  const fmt = (n) => privacy ? "•••••" : money.format(n || 0);
  const fmt2 = (n) => privacy ? "•••••" : money2.format(n || 0);

  return (
    <AppShell>
      <main className="page pd-shell" data-testid="portfolio-page">
        <header className="projects-hero">
          <div>
            <small className="eyebrow">PATRIMONIO</small>
            <h1>Tu vida financiera, <em>todo en un lugar.</em></h1>
            <p>Rastrea inversiones, propiedades, deudas y net worth. Precios manuales por ahora; arquitectura lista para conectar APIs de mercado.</p>
          </div>
          <div className="head-actions">
            <button
              type="button"
              onClick={togglePrivacy}
              className="cash-btn ghost"
              data-testid="privacy-toggle"
              title={privacy ? "Mostrar valores" : "Ocultar valores"}
            >{privacy ? <EyeOff size={14} /> : <Eye size={14} />} {privacy ? "Privado" : "Público"}</button>
            <a
              href={`${api.defaults.baseURL}/exports/portfolio/pdf`}
              target="_blank" rel="noreferrer"
              className="cash-btn ghost"
              data-testid="portfolio-pdf"
              title="Descargar reporte PDF"
            ><FileDown size={14} /> PDF</a>
            <button
              type="button"
              onClick={() => { setForm({ status: "active", currency: "MXN" }); setModal("asset"); }}
              className="expenses-add-button"
              data-testid="new-asset-btn"
            ><Plus size={16} /> Nuevo activo</button>
          </div>
        </header>

        <nav className="pd-tabs" data-testid="portfolio-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} className={tab === t.id ? "active" : ""} onClick={() => setTab(t.id)} data-testid={`portfolio-tab-${t.id}`}>
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </nav>

        {error && <p className="expenses-error" data-testid="portfolio-error">{error}</p>}

        {loading ? (
          <div className="pd-kpi-grid">{[1,2,3,4,5,6,7].map((k) => <div key={k} className="skeleton" style={{ height: 84 }} />)}</div>
        ) : tab === "dashboard" ? (
          <DashboardTab dashboard={dashboard} fmt={fmt} fmt2={fmt2} onOpen={(id) => navigate(`/patrimonio/${id}`)} />
        ) : tab === "assets" ? (
          <AssetsTab assets={assets} meta={meta} fmt={fmt} fmt2={fmt2} onOpen={(id) => navigate(`/patrimonio/${id}`)} onDelete={deleteAsset} onEdit={editAsset} onAdd={() => { setForm({ status: "active", currency: "MXN" }); setModal("asset"); }} />
        ) : tab === "liabilities" ? (
          <LiabilitiesTab liabilities={liabilities} fmt={fmt} fmt2={fmt2} onDelete={deleteLiability} onEdit={editLiability} onAdd={() => { setForm({ currency: "MXN", type: "loan" }); setModal("liability"); }} />
        ) : (
          <NetWorthTab netWorth={netWorth} fmt={fmt} fmt2={fmt2} onSaveSnapshot={saveSnapshot} />
        )}

        {modal === "asset" && <AssetModal form={form} setForm={setForm} meta={meta} onClose={() => setModal(null)} onSubmit={createAsset} />}
        {modal === "liability" && <LiabilityModal form={form} setForm={setForm} meta={meta} onClose={() => setModal(null)} onSubmit={createLiability} />}
      </main>
    </AppShell>
  );
}

// ============ DASHBOARD TAB ============
function DashboardTab({ dashboard, fmt, fmt2, onOpen }) {
  if (!dashboard) return null;
  const t = dashboard.totals;
  const isPos = (n) => (n || 0) >= 0;

  if (t.assets_count === 0 && t.liabilities_count === 0) {
    return (
      <div className="pd-empty" data-testid="portfolio-empty">
        <div className="pd-empty-icon"><Gem size={26} /></div>
        <h3>Aún no registras tu patrimonio.</h3>
        <p>Agrega tu primer activo (acciones, cripto, casa, negocio) para empezar a ver tu net worth y rendimiento.</p>
      </div>
    );
  }

  const catAlloc = dashboard.allocation.by_category.map((r) => ({ ...r, name: CATEGORY_LABELS[r.label] || r.label }));

  return (
    <section data-testid="portfolio-dashboard">
      <div className="pd-kpi-grid">
        <div className="pd-kpi contract" data-testid="kpi-portfolio-value"><small><Briefcase size={12} /> Portfolio total</small><b>{fmt(t.portfolio_value_mxn)}</b><small className="delta">{t.assets_count} activos</small></div>
        <div className={`pd-kpi ${isPos(t.unrealized_gain_mxn) ? "collected" : "expense"}`} data-testid="kpi-unrealized"><small>{isPos(t.unrealized_gain_mxn) ? <TrendingUp size={12} /> : <TrendingDown size={12} />} G/L no realizada</small><b style={{ color: isPos(t.unrealized_gain_mxn) ? "#10b981" : "#ef4444" }}>{fmt(t.unrealized_gain_mxn)}</b></div>
        <div className={`pd-kpi ${isPos(t.realized_gain_mxn) ? "collected" : "expense"}`} data-testid="kpi-realized"><small>G/L realizada</small><b style={{ color: isPos(t.realized_gain_mxn) ? "#10b981" : "#ef4444" }}>{fmt(t.realized_gain_mxn)}</b></div>
        <div className="pd-kpi contract" data-testid="kpi-cash"><small><Wallet size={12} /> Efectivo</small><b>{fmt(t.cash_balance_mxn)}</b></div>
        <div className="pd-kpi income" data-testid="kpi-invested"><small>Invertido (costo)</small><b>{fmt(t.cost_basis_mxn)}</b></div>
        <div className="pd-kpi expense" data-testid="kpi-liabilities"><small><CreditCard size={12} /> Pasivos</small><b>{fmt(t.total_liabilities_mxn)}</b><small className="delta">{t.liabilities_count} deuda(s)</small></div>
        <div className="pd-kpi profit" data-testid="kpi-net-worth"><small>💎 Net Worth</small><b style={{ color: isPos(t.net_worth_mxn) ? "#10b981" : "#ef4444", fontSize: 26 }}>{fmt(t.net_worth_mxn)}</b></div>
      </div>

      <section style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 20, marginBottom: 22 }}>
        <div style={{ background: "#fff", borderRadius: 18, padding: 22, border: "1px solid #e2e8f0" }} data-testid="chart-allocation">
          <h3 style={{ margin: "0 0 14px", font: "900 16px Nunito, sans-serif" }}>Allocation por categoría</h3>
          {catAlloc.length === 0 ? <p style={{ color: "#94a3b8" }}>Sin datos.</p> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={catAlloc} dataKey="value_mxn" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={2}>
                  {catAlloc.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => money.format(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div style={{ background: "#fff", borderRadius: 18, padding: 22, border: "1px solid #e2e8f0" }} data-testid="top-holdings">
          <h3 style={{ margin: "0 0 14px", font: "900 16px Nunito, sans-serif" }}>Top holdings</h3>
          {dashboard.top_holdings.length === 0 ? <p style={{ color: "#94a3b8" }}>Sin activos.</p> : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {dashboard.top_holdings.map((a) => (
                <li key={a.id} style={{ padding: "10px 0", borderBottom: "1px dashed #e2e8f0", cursor: "pointer" }} onClick={() => onOpen(a.id)} data-testid={`top-holding-${a.id}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <b style={{ display: "block", font: "800 14px Nunito, sans-serif" }}>{a.name} {a.ticker && <span style={{ color: "#94a3b8" }}>({a.ticker})</span>}</b>
                      <small style={{ color: "#64748b" }}>{CATEGORY_LABELS[a.category] || a.category}</small>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <b>{fmt2(a.value_mxn)}</b>
                      <div style={{ color: (a.holdings.pct_return || 0) >= 0 ? "#10b981" : "#ef4444", fontSize: 12, fontWeight: 700 }}>{a.holdings.pct_return}%</div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div style={{ background: "#fff", borderRadius: 18, padding: 22, border: "1px solid #e2e8f0" }} data-testid="top-gainers">
          <h3 style={{ margin: "0 0 12px", font: "900 15px Nunito, sans-serif", color: "#10b981" }}>🚀 Ganadores</h3>
          {dashboard.top_gainers.length === 0 ? <p style={{ color: "#94a3b8", fontSize: 13 }}>Aún sin ganancias.</p> :
            dashboard.top_gainers.map((a) => (
              <div key={a.id} style={{ padding: "8px 0", borderBottom: "1px dashed #e2e8f0", display: "flex", justifyContent: "space-between" }}>
                <span><b>{a.name}</b><small style={{ color: "#64748b", marginLeft: 6 }}>{a.ticker}</small></span>
                <b style={{ color: "#10b981" }}>+{a.holdings.pct_return}%</b>
              </div>
            ))}
        </div>
        <div style={{ background: "#fff", borderRadius: 18, padding: 22, border: "1px solid #e2e8f0" }} data-testid="top-losers">
          <h3 style={{ margin: "0 0 12px", font: "900 15px Nunito, sans-serif", color: "#ef4444" }}>📉 Perdedores</h3>
          {dashboard.top_losers.length === 0 ? <p style={{ color: "#94a3b8", fontSize: 13 }}>Nada rojo por aquí. 👏</p> :
            dashboard.top_losers.map((a) => (
              <div key={a.id} style={{ padding: "8px 0", borderBottom: "1px dashed #e2e8f0", display: "flex", justifyContent: "space-between" }}>
                <span><b>{a.name}</b><small style={{ color: "#64748b", marginLeft: 6 }}>{a.ticker}</small></span>
                <b style={{ color: "#ef4444" }}>{a.holdings.pct_return}%</b>
              </div>
            ))}
        </div>
      </section>
    </section>
  );
}

// ============ ASSETS TAB ============
function AssetsTab({ assets, meta, fmt, fmt2, onOpen, onDelete, onEdit, onAdd }) {
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");
  const [profitFilter, setProfitFilter] = useState("all");
  const filtered = useMemo(() => {
    return assets.filter((a) => {
      if (cat !== "all" && a.category !== cat) return false;
      if (profitFilter === "gainers" && (a.holdings?.unrealized_gain || 0) <= 0) return false;
      if (profitFilter === "losers" && (a.holdings?.unrealized_gain || 0) >= 0) return false;
      if (search && !((a.name + " " + (a.ticker || "") + " " + (a.institution || "")).toLowerCase().includes(search.toLowerCase()))) return false;
      return true;
    });
  }, [assets, cat, search, profitFilter]);

  return (
    <section data-testid="assets-tab">
      <div className="projects-toolbar">
        <div className="search" style={{ flex: 1 }}>
          <Search size={16} color="#94a3b8" />
          <input placeholder="Buscar activo, ticker o institución…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="assets-search" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="assets-filter-cat" style={{ padding: "10px 14px", borderRadius: 12, border: "1px solid #e2e8f0", font: "600 13px Nunito, sans-serif", background: "#fff" }}>
          <option value="all">Todas las categorías</option>
          {(meta?.categories || []).map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
        </select>
        <button onClick={onAdd} className="cash-btn primary" data-testid="assets-add-btn"><Plus size={14} /> Nuevo</button>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button className={`filter-chip ${profitFilter === "all" ? "active" : ""}`} onClick={() => setProfitFilter("all")} data-testid="filter-profit-all">Todos</button>
        <button className={`filter-chip ${profitFilter === "gainers" ? "active" : ""}`} onClick={() => setProfitFilter("gainers")} data-testid="filter-profit-gainers" style={profitFilter === "gainers" ? { background: "#10b981", borderColor: "#10b981" } : {}}><TrendingUp size={12} /> Ganadores</button>
        <button className={`filter-chip ${profitFilter === "losers" ? "active" : ""}`} onClick={() => setProfitFilter("losers")} data-testid="filter-profit-losers" style={profitFilter === "losers" ? { background: "#ef4444", borderColor: "#ef4444" } : {}}><TrendingDown size={12} /> Perdedores</button>
      </div>

      {filtered.length === 0 ? (
        <div className="pd-empty" data-testid="assets-empty">
          <div className="pd-empty-icon"><Gem size={22} /></div>
          <h3>{assets.length === 0 ? "Aún no registras activos." : "Nada con ese filtro."}</h3>
          <p>{assets.length === 0 ? "Agrega tu primera acción, cripto, propiedad o negocio." : "Prueba con otra búsqueda."}</p>
          {assets.length === 0 && <button onClick={onAdd} className="cash-btn primary"><Plus size={14} /> Crear primer activo</button>}
        </div>
      ) : (
        <div className="files-grid">
          {filtered.map((a) => {
            const gain = a.holdings.unrealized_gain || 0;
            return (
              <article key={a.id} className="file-card" data-testid={`asset-card-${a.id}`} style={{ cursor: "pointer" }} onClick={() => onOpen(a.id)}>
                <span className="file-cat" style={{ background: gain >= 0 ? "#10b981" : "#ef4444" }}>{a.holdings.pct_return}%</span>
                <div style={{ padding: 4 }}>
                  <b style={{ font: "900 16px Nunito, sans-serif", color: "#0f172a" }}>{a.name}</b>
                  {a.ticker && <div style={{ font: "700 12px Nunito, sans-serif", color: "#64748b" }}>{a.ticker}</div>}
                </div>
                <small>{CATEGORY_LABELS[a.category] || a.category} · {a.institution || "—"}</small>
                <div style={{ padding: "10px 8px", background: "#f8fafc", borderRadius: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: "#64748b" }}>Cantidad</span>
                    <b>{a.holdings.quantity}</b>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: "#64748b" }}>Valor ({a.currency})</span>
                    <b>{fmt2(a.holdings.current_value)}</b>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                    <span style={{ color: "#64748b" }}>En MXN</span>
                    <b style={{ color: "#4169e1" }}>{fmt(a.value_mxn)}</b>
                  </div>
                </div>
                <div className="file-actions">
                  <a onClick={(e) => { e.stopPropagation(); onOpen(a.id); }} data-testid={`asset-open-${a.id}`}><ArrowUpRight size={12} /> Abrir</a>
                  <button onClick={(e) => { e.stopPropagation(); onEdit(a); }} data-testid={`asset-edit-${a.id}`} title="Editar"><Edit3 size={12} /></button>
                  <button className="del" onClick={(e) => { e.stopPropagation(); onDelete(a.id); }} data-testid={`asset-delete-${a.id}`}><Trash2 size={12} /></button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ============ LIABILITIES TAB ============
function LiabilitiesTab({ liabilities, fmt, fmt2, onDelete, onEdit, onAdd }) {
  return (
    <section data-testid="liabilities-tab">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ margin: 0, font: "900 18px Nunito, sans-serif" }}>Pasivos totales: <span style={{ color: "#ef4444" }}>{fmt(liabilities.reduce((s, l) => s + (l.current_balance_mxn || 0), 0))}</span></h3>
        <button onClick={onAdd} className="cash-btn primary" data-testid="liabilities-add-btn"><Plus size={14} /> Nuevo pasivo</button>
      </div>

      {liabilities.length === 0 ? (
        <div className="pd-empty" data-testid="liabilities-empty">
          <div className="pd-empty-icon"><CreditCard size={22} /></div>
          <h3>Sin deudas registradas.</h3>
          <p>Agrega préstamos, hipotecas o tarjetas para calcular tu net worth real.</p>
          <button onClick={onAdd} className="cash-btn primary"><Plus size={14} /> Agregar pasivo</button>
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 18, border: "1px solid #e2e8f0", overflow: "hidden" }}>
          <table className="expenses-table" style={{ margin: 0 }} data-testid="liabilities-table">
            <thead>
              <tr>
                <th>Nombre</th><th>Tipo</th><th>Institución</th><th>Tasa</th><th style={{ textAlign: "right" }}>Saldo</th><th style={{ textAlign: "right" }}>En MXN</th><th></th>
              </tr>
            </thead>
            <tbody>
              {liabilities.map((l) => (
                <tr key={l.id} data-testid={`liability-row-${l.id}`}>
                  <td><b>{l.name}</b></td>
                  <td>{LIAB_LABELS[l.type] || l.type}</td>
                  <td>{l.institution || "—"}</td>
                  <td>{l.interest_rate ? `${l.interest_rate}%` : "—"}</td>
                  <td style={{ textAlign: "right", color: "#ef4444", fontWeight: 800 }}>{fmt2(l.current_balance)} <small style={{ color: "#94a3b8" }}>{l.currency}</small></td>
                  <td style={{ textAlign: "right" }}>{fmt(l.current_balance_mxn)}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button className="cash-btn ghost" style={{ padding: "6px 10px", color: "#4169e1", marginRight: 4 }} onClick={() => onEdit(l)} data-testid={`liability-edit-${l.id}`}><Edit3 size={13} /></button>
                    <button className="cash-btn ghost" style={{ padding: "6px 10px", color: "#ef4444" }} onClick={() => onDelete(l.id)} data-testid={`liability-delete-${l.id}`}><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ============ NET WORTH TAB ============
function NetWorthTab({ netWorth, fmt, fmt2, onSaveSnapshot }) {
  if (!netWorth) return null;
  const isPos = (netWorth.net_worth_mxn || 0) >= 0;
  const history = (netWorth.history || []).map((h) => ({ ...h, dateLabel: h.date }));

  return (
    <section data-testid="networth-tab">
      <div className="pd-kpi-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        <div className="pd-kpi collected" data-testid="nw-assets"><small>Activos totales</small><b>{fmt(netWorth.total_assets_mxn)}</b></div>
        <div className="pd-kpi expense" data-testid="nw-liabs"><small>Pasivos totales</small><b>{fmt(netWorth.total_liabilities_mxn)}</b></div>
        <div className="pd-kpi profit" data-testid="nw-net"><small>💎 Net Worth</small><b style={{ fontSize: 28, color: isPos ? "#10b981" : "#ef4444" }}>{fmt(netWorth.net_worth_mxn)}</b></div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <button onClick={onSaveSnapshot} className="cash-btn ghost" data-testid="save-snapshot"><LineChartIcon size={14} /> Guardar snapshot de hoy</button>
      </div>

      {history.length > 1 && (
        <div style={{ background: "#fff", borderRadius: 18, padding: 22, border: "1px solid #e2e8f0", marginBottom: 22 }} data-testid="networth-chart">
          <h3 style={{ margin: "0 0 14px", font: "900 16px Nunito, sans-serif" }}>Evolución de tu Net Worth</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={history}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="dateLabel" style={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} style={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => money.format(v)} />
              <Legend />
              <Line type="monotone" dataKey="total_assets" name="Activos" stroke="#10b981" strokeWidth={2} />
              <Line type="monotone" dataKey="total_liabilities" name="Pasivos" stroke="#ef4444" strokeWidth={2} />
              <Line type="monotone" dataKey="net_worth" name="Net Worth" stroke="#4169e1" strokeWidth={3} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div style={{ background: "#fff", borderRadius: 18, padding: 22, border: "1px solid #e2e8f0" }}>
          <h3 style={{ margin: "0 0 12px", font: "900 15px Nunito, sans-serif", color: "#10b981" }}>Activos por categoría</h3>
          {netWorth.assets_breakdown.length === 0 ? <p style={{ color: "#94a3b8" }}>Sin activos.</p> :
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={netWorth.assets_breakdown.map((r) => ({ ...r, name: CATEGORY_LABELS[r.label] || r.label }))} layout="vertical">
                <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} style={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" style={{ fontSize: 11 }} width={100} />
                <Tooltip formatter={(v) => money.format(v)} />
                <Bar dataKey="value_mxn" fill="#10b981" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          }
        </div>
        <div style={{ background: "#fff", borderRadius: 18, padding: 22, border: "1px solid #e2e8f0" }}>
          <h3 style={{ margin: "0 0 12px", font: "900 15px Nunito, sans-serif", color: "#ef4444" }}>Pasivos por tipo</h3>
          {netWorth.liabilities_breakdown.length === 0 ? <p style={{ color: "#94a3b8" }}>Sin pasivos.</p> :
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={netWorth.liabilities_breakdown.map((r) => ({ ...r, name: LIAB_LABELS[r.label] || r.label }))} layout="vertical">
                <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} style={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" style={{ fontSize: 11 }} width={100} />
                <Tooltip formatter={(v) => money.format(v)} />
                <Bar dataKey="value_mxn" fill="#ef4444" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          }
        </div>
      </div>
    </section>
  );
}

// ============ MODALS ============
const CURRENCIES = ["MXN", "USD", "EUR", "GBP", "CAD", "JPY", "BTC", "ETH"];

function AssetModal({ form, setForm, meta, onClose, onSubmit }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="cash-modal-backdrop" onClick={onClose} data-testid="asset-modal">
      <div className="cash-modal" onClick={(e) => e.stopPropagation()} style={{ width: "min(760px, 96vw)" }}>
        <header><div><small>{form.id ? "EDITAR ACTIVO" : "NUEVO ACTIVO"}</small><b>{form.id ? "Actualizar información" : "Registro manual"}</b></div><button onClick={onClose}><X size={18} /></button></header>
        <div style={{ padding: "20px 24px 8px" }}>
          <div className="cash-modal-grid">
            <label className="full">Nombre *<input value={form.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Ej: Apple Inc., Casa Polanco, BTC" data-testid="asset-name" /></label>
            <label>Ticker (opcional)<input value={form.ticker || ""} onChange={(e) => set("ticker", e.target.value.toUpperCase())} placeholder="Ej: AAPL" data-testid="asset-ticker" /></label>
            <label>Categoría *
              <select value={form.category || ""} onChange={(e) => set("category", e.target.value)} data-testid="asset-category">
                <option value="">Elige categoría…</option>
                {(meta?.categories || []).map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
              </select>
            </label>
            <label>Institución<input value={form.institution || ""} onChange={(e) => set("institution", e.target.value)} placeholder="Ej: Fidelity, Coinbase, BBVA" data-testid="asset-institution" /></label>
            <label>Storage
              <select value={form.storage_location || ""} onChange={(e) => set("storage_location", e.target.value)}>
                <option value="">Selecciona ubicación…</option>
                {(meta?.storage_locations || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label>Cantidad<input type="number" step="any" value={form.current_quantity || ""} onChange={(e) => set("current_quantity", e.target.value)} placeholder="Ej: 10" data-testid="asset-quantity" /></label>
            <label>Precio compra<input type="number" step="0.01" value={form.purchase_price || ""} onChange={(e) => set("purchase_price", e.target.value)} placeholder="Ej: 150.00" data-testid="asset-purchase-price" /></label>
            <label>Precio actual<input type="number" step="0.01" value={form.current_price || ""} onChange={(e) => set("current_price", e.target.value)} placeholder="Ej: 180.00" data-testid="asset-current-price" /></label>
            <label>Moneda
              <select value={form.currency || "MXN"} onChange={(e) => set("currency", e.target.value)} data-testid="asset-currency">
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label>Fecha de compra<input type="date" value={form.purchase_date || ""} onChange={(e) => set("purchase_date", e.target.value)} /></label>
            <label className="full">Notas<textarea rows={3} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} placeholder="Ej: Tesis de inversión, plan de salida, observaciones…" /></label>
          </div>
        </div>
        <div className="cash-modal-actions">
          <button onClick={onClose} className="cash-btn ghost">Cancelar</button>
          <button onClick={onSubmit} className="cash-btn primary" disabled={!form.name || !form.category} data-testid="asset-save">{form.id ? "Guardar cambios" : "Crear activo"} <ArrowRight size={13} /></button>
        </div>
      </div>
    </div>
  );
}

function LiabilityModal({ form, setForm, meta, onClose, onSubmit }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="cash-modal-backdrop" onClick={onClose} data-testid="liability-modal">
      <div className="cash-modal" onClick={(e) => e.stopPropagation()} style={{ width: "min(640px, 96vw)" }}>
        <header><div><small>{form.id ? "EDITAR PASIVO" : "NUEVO PASIVO"}</small><b>{form.id ? "Actualizar deuda" : "Deuda / obligación"}</b></div><button onClick={onClose}><X size={18} /></button></header>
        <div style={{ padding: "20px 24px 8px" }}>
          <div className="cash-modal-grid">
            <label className="full">Nombre *<input value={form.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Ej: Hipoteca casa, Tarjeta BBVA" data-testid="liability-name" /></label>
            <label>Tipo *
              <select value={form.type || "loan"} onChange={(e) => set("type", e.target.value)} data-testid="liability-type">
                {(meta?.liability_types || []).map((t) => <option key={t} value={t}>{LIAB_LABELS[t] || t}</option>)}
              </select>
            </label>
            <label>Institución<input value={form.institution || ""} onChange={(e) => set("institution", e.target.value)} placeholder="Ej: Banorte, Santander" data-testid="liability-institution" /></label>
            <label>Saldo actual *<input type="number" step="0.01" value={form.current_balance || ""} onChange={(e) => set("current_balance", e.target.value)} placeholder="Ej: 800000" data-testid="liability-balance" /></label>
            <label>Moneda
              <select value={form.currency || "MXN"} onChange={(e) => set("currency", e.target.value)}>
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label>Tasa % anual<input type="number" step="0.01" value={form.interest_rate || ""} onChange={(e) => set("interest_rate", e.target.value)} placeholder="Ej: 9.5" /></label>
            <label>Pago mensual<input type="number" step="0.01" value={form.monthly_payment || ""} onChange={(e) => set("monthly_payment", e.target.value)} placeholder="Ej: 8500" /></label>
          </div>
        </div>
        <div className="cash-modal-actions">
          <button onClick={onClose} className="cash-btn ghost">Cancelar</button>
          <button onClick={onSubmit} className="cash-btn primary" disabled={!form.name || !form.current_balance} data-testid="liability-save">{form.id ? "Guardar cambios" : "Guardar"}</button>
        </div>
      </div>
    </div>
  );
}
